import React, { useEffect, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";

interface SegmentFilters {
  audienceType: string;
  criteriaCount: string;
}

interface SegmentFilterProps {
  audienceType: string;
  criteriaCount: string;
  audienceTypeList: Array<{ label: string; value: string }>;
  criteriaCountList: Array<{ label: string; value: string }>;
  onApplyFilters: (filters: SegmentFilters) => void;
  onResetFilters: () => void;
}

export const SegmentFilter: React.FC<SegmentFilterProps> = ({
  audienceType,
  criteriaCount,
  audienceTypeList,
  criteriaCountList,
  onApplyFilters,
  onResetFilters,
}) => {
  // Transform audience type list to options format
  const audienceTypeOptions = useMemo(
    () =>
      audienceTypeList?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [audienceTypeList]
  );

  // Transform criteria count list to options format
  const criteriaCountOptions = useMemo(
    () =>
      criteriaCountList?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [criteriaCountList]
  );

  // Define filter fields
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "audienceType",
        label: "Audience Type",
        type: "select",
        placeholder: "Select Audience Type",
        options: audienceTypeOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "criteriaCount",
        label: "Criteria Count",
        type: "select",
        placeholder: "Select Criteria Count",
        options: criteriaCountOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
    ],
    [audienceTypeOptions, criteriaCountOptions]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Sync form values when URL params change
  useEffect(() => {
    form.reset({
      audienceType: decodeURIComponent(audienceType) || "",
      criteriaCount: decodeURIComponent(criteriaCount) || "",
    });
  }, [audienceType, criteriaCount, form]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();
    onApplyFilters({
      audienceType: formValues.audienceType || "",
      criteriaCount: formValues.criteriaCount || "",
    });
  };

  const handleFilterReset = () => {
    form.reset({
      audienceType: "",
      criteriaCount: "",
    });
    onResetFilters();
  };

  const handleFieldChange = (field: string, value: string) => {
    // Add any field-specific logic here if needed
  };

  return (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        onFieldChange={handleFieldChange}
        gridColumns={2}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            buttonContainer: "flex justify-end gap-4",
          },
        }}
      />
    </div>
  );
};